import { Suspense, lazy, useEffect, useState } from 'react';
import {
  ESCALA_DA_ENTRADA,
  PILHAS_DE_FONTE,
  estruturaResolvida,
  textoSobre,
} from '@vestigo/core/area-estruturas-da-entrada';
import { api, type TelaDeEntrada } from '../lib/api.js';
import { slugDaEntrada } from '../lib/endereco.js';
import { caraSemeada, caraValida } from '../lib/semente.js';
import { variaveisDoTema } from '../lib/tema.js';
import { Sobria, Lateral, type PropsDaEstrutura } from './Basicas.js';
import { telaComDicionario } from '../lib/tela-com-dicionario.js';

/**
 * ── A MOLDURA DA TELA DE ENTRAR (leva L1 da bancada) ─────────────────────────
 *
 * As três rotas de entrada (`/entrar`, `/primeiro-acesso`, `/esqueci-senha`)
 * passam por aqui. A moldura busca a «cara» da área (ou recebe-a por prop, na
 * prévia da bancada) e escolhe a ESTRUTURA — uma das seis de
 * `estruturas-da-entrada.ts`. Sóbria e Lateral são as de sempre e ficam no
 * pedaço de entrada; as outras quatro vêm por `import()` só quando a área as
 * escolheu (V13: a tela de entrar É o pedaço que a régua orça).
 *
 * ## O «flash» que este cabeçalho documentava DEIXOU de existir (`CE3`)
 *
 * Aqui estava escrito, e por muito tempo: *«enquanto o pedaço chega, Sóbria
 * desenha — o flash já existia, porque a cara chega depois do primeiro
 * desenho»*. O dono relatou-o em 20/09/2026 como defeito, e era um: a tela
 * abria em `#0b0b0f`/`#22c55e`/`sobria` e saltava para o que a área tem.
 *
 * Duas mudanças fecham-no, e cada uma trata uma das duas causas:
 *
 * - **a cara chegava por `fetch`** — passou a vir semeada no documento, lida de
 *   forma síncrona no inicializador do `useState` (`lib/semente.ts`);
 * - **o pedaço chegava por `import()`** — e para esse não há semente possível,
 *   então o `fallback` deixou de ser a estrutura errada e passou a ser o fundo
 *   da área. Ver o `Suspense` no fim do ficheiro.
 *
 * O que a estrutura recebe são PEÇAS NOMEADAS, não `children` soltos: o
 * formulário e, quando há, o bloco secundário («É a sua primeira vez?») — é o
 * que permite à Faixa pô-lo na coluna da direita e ao Cartão dentro do cartão.
 */
const Estruturas = telaComDicionario(() => import('./Estruturas.js'));

export interface PecasDaEntrada {
  /** O `<h1>` funcional da tela: «Entrar», «Primeiro acesso»… */
  titulo: string;
  intro?: React.ReactNode;
  formulario: React.ReactNode;
  secundario?: React.ReactNode;
  rodape?: React.ReactNode;
}

export function useCaraDaEntrada(cara?: TelaDeEntrada | null): TelaDeEntrada | null {
  const slug = slugDaEntrada();
  /*
   * ── O ESTADO NASCE SEMEADO, E É ISSO QUE MATA O PISCAR (`CE3`) ────────────
   *
   * O `useState` recebia `null` e o `useEffect` preenchia-o — e um `useEffect`
   * corre DEPOIS do primeiro desenho. Esse primeiro desenho era a cara de
   * fábrica: fundo `#0b0b0f`, destaque `#22c55e`, estrutura `sobria`. O
   * comentário do cabeçalho deste ficheiro dizia-o com todas as letras —
   * *«enquanto o pedaço chega, Sóbria desenha — o flash já existia»*.
   *
   * `caraSemeada` devolve o que o servidor injetou no documento para ESTE slug,
   * de forma síncrona. Quando ela existe, o primeiro desenho já é o certo e o
   * `fetch` abaixo só confirma; quando não existe (desenvolvimento, seletor de
   * áreas, slug trocado no meio da sessão), nada muda em relação a antes.
   *
   * O inicializador é uma FUNÇÃO porque ele lê `window`: passar o valor
   * chamaria `caraSemeada` em todo render para o descartar em todos menos o
   * primeiro.
   */
  const [buscada, setBuscada] = useState<TelaDeEntrada | null>(() =>
    slug ? caraSemeada(slug) : null,
  );
  useEffect(() => {
    if (cara !== undefined || !slug) return;
    let vivo = true;
    void api
      .get<TelaDeEntrada>(`/entrada/${encodeURIComponent(slug)}`)
      /* Falha silenciosa de propósito: a tela de entrar tem de entrar mesmo que
         a personalização não venha. Com semente, «não venha» deixa de ser tela
         de fábrica e passa a ser a cara que o documento já trouxe. */
      .then((c) => {
        /*
         * ── A MESMA GUARDA DA SEMENTE, AGORA TAMBÉM AQUI (23/09/2026) ──────
         *
         * `caraValida` existia e guardava só o `<script>` do documento. Um
         * corpo sem `entrada` vindo da rede passava direto para o estado, e o
         * render seguinte lançava em `cara?.entrada.estrutura` — o `?` está no
         * `cara`, não no `entrada`. Recusar devolve a tela ao que ela já tinha
         * (a semente, ou o de fábrica), que é uma tela que abre.
         */
        if (vivo && caraValida(c)) setBuscada(c);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [cara, slug]);
  /* A cara DADA por prop (a prévia da bancada) passa pela mesma peneira: ela
     vem de um editor, e um documento a meio de ser escrito é exatamente o caso
     em que `entrada` ainda não existe. */
  if (cara === undefined) return buscada;
  return cara !== null && caraValida(cara) ? cara : null;
}

/** As variáveis da tela: o tema, as cores sobrescritas, a fonte da voz, a escala. */
export function variaveisDaEntrada(cara: TelaDeEntrada | null): React.CSSProperties {
  const tema = { ...(cara?.tema ?? {}) };
  const cores = cara?.entrada.cores;
  if (cores?.fundo) tema.fundo = cores.fundo;
  if (cores?.superficie) tema.superficie = cores.superficie;
  if (cores?.destaque) tema.destaque = cores.destaque;
  if (cores?.texto) tema.texto = cores.texto;
  if (cara?.entrada.raio !== null && cara?.entrada.raio !== undefined)
    tema.raio = cara.entrada.raio;
  const base = variaveisDoTema(tema) as Record<string, string>;
  return {
    ...base,
    '--sobre-destaque': textoSobre(base['--destaque'] ?? '#22c55e'),
    '--fonte-da-voz': PILHAS_DE_FONTE[cara?.entrada.fonte ?? 'sistema'],
    '--escala': String(ESCALA_DA_ENTRADA[cara?.entrada.tamanho ?? 'normal']),
  } as React.CSSProperties;
}

export function MolduraDaEntrada({
  pecas: pecasDadas,
  cara: caraDada,
  inerte = false,
}: {
  /**
   * As peças — ou uma FUNÇÃO da cara, para a peça que precisa de saber algo da
   * área (`CE3`: a tela de entrar só desenha a porta do cadastro quando ela
   * está aberta).
   *
   * Uma função, e não uma prop nova em cada chamador, porque a moldura já tem a
   * cara na mão: passá-la a quem a pedir é uma linha, e obrigar `Entrar` a
   * buscá-la por conta própria seria o segundo pedido da mesma resposta — e o
   * segundo sítio a decidir o que a tela mostra.
   */
  pecas: PecasDaEntrada | ((cara: TelaDeEntrada | null) => PecasDaEntrada);
  /**
   * A prévia da bancada manda a cara por prop; as rotas de entrada buscam.
   *
   * `rotas/Cadastro.tsx` também a manda — ela tem `fetch` próprio, porque
   * precisa de distinguir «porta fechada» de «rede má», e é por isso que a
   * semente do cadastro é lida LÁ e não aqui.
   */
  cara?: TelaDeEntrada | null;
  inerte?: boolean;
}) {
  const cara = useCaraDaEntrada(caraDada);
  const pecas = typeof pecasDadas === 'function' ? pecasDadas(cara) : pecasDadas;
  const estrutura = estruturaResolvida({
    estrutura: cara?.entrada.estrutura,
    orientacao: cara?.entrada.orientacao,
    imagem: cara?.entrada.imagem,
  });
  const props: PropsDaEstrutura = { cara, pecas, inerte };

  useEffect(() => {
    /* A Literata só se paga quando escolhida — e nunca pelo `index.html`. */
    if (cara?.entrada.fonte === 'literata') {
      void import('../lib/fonte-de-leitura.js').then((m) => m.garantirFonteDeLeitura());
    }
  }, [cara?.entrada.fonte]);

  return (
    <div
      className="min-h-screen"
      style={{ ...variaveisDaEntrada(cara), fontFamily: 'var(--fonte-da-voz)' }}
      data-vst-estrutura={estrutura}
    >
      {estrutura === 'sobria' ? (
        <Sobria {...props} />
      ) : estrutura === 'lateral' ? (
        <Lateral {...props} />
      ) : (
        /*
         * ── O FALLBACK DEIXOU DE SER A SÓBRIA (`CE3`) ─────────────────────
         *
         * Enquanto o pedaço das quatro estruturas chega, isto desenhava
         * `<Sobria>` — a tela INTEIRA na estrutura errada, que depois salta para
         * a certa. É o mesmo piscar que a semente foi fechar, só que causado
         * pelo `import()` em vez do `fetch`, e a semente não o alcança: nenhuma
         * injeção no documento faz um pedaço de JavaScript chegar antes de
         * chegar.
         *
         * O que fica no lugar é o fundo da área, já com a cor certa, por uns
         * poucos quadros. Uma tela sem conteúdo por 50 ms lê-se como
         * carregamento; uma tela com o layout errado por 50 ms lê-se como
         * defeito — e é a segunda que o dono relatou.
         */
        <Suspense fallback={<div className="min-h-screen" aria-hidden />}>
          <Estruturas estrutura={estrutura} {...props} />
        </Suspense>
      )}
    </div>
  );
}
